import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Activity, CheckCircle2, XCircle, Clock, RefreshCw, TrendingUp, AlertCircle, Calendar, Download, Trash2, FileText, AlertTriangle, Mail, Send } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  getLocalOutages, 
  getOutageStats, 
  exportOutagesToCSV, 
  exportOutagesToJSON, 
  clearAllOutages,
  cleanOldOutages,
  retryPendingReports
} from '../services/outageService';

const EstadoSistema = () => {
  const { API, user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [systemData, setSystemData] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [exportingCalendar, setExportingCalendar] = useState(false);
  // Banner PWA
  const [pwaBannerActivo, setPwaBannerActivo] = useState(false);
  const [savingPwaBanner, setSavingPwaBanner] = useState(false);
  
  // Estados para el registro de caídas
  const [outages, setOutages] = useState([]);
  const [outageStats, setOutageStats] = useState(null);
  const [showAllOutages, setShowAllOutages] = useState(false);
  
  // Estados para reportes enviados a soporte
  const [outageReports, setOutageReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [retryingReports, setRetryingReports] = useState(false);
  const [sendingConsolidated, setSendingConsolidated] = useState(false);
  const [clearingDbReports, setClearingDbReports] = useState(false);

  // Borrar todos los reportes de la base de datos
  const handleClearDbReports = async () => {
    if (!window.confirm('¿Estás seguro de BORRAR TODOS los registros de la base de datos? Esta acción no se puede deshacer.')) {
      return;
    }
    
    setClearingDbReports(true);
    try {
      const response = await axios.delete(`${API}/system/outage-reports`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.data.success) {
        toast.success(`✅ Se eliminaron ${response.data.deleted_count} registros de la BD`);
        loadOutageReports();
      } else {
        toast.error(response.data.error || 'Error al borrar');
      }
    } catch (error) {
      console.error('Error borrando reportes:', error);
      toast.error('Error al borrar registros');
    } finally {
      setClearingDbReports(false);
    }
  };

  // Enviar informe consolidado a soporte
  const handleSendConsolidatedReport = async () => {
    if (!window.confirm('¿Enviar UN informe consolidado con TODAS las caídas pendientes a support@emergent.sh?')) {
      return;
    }
    
    setSendingConsolidated(true);
    try {
      const response = await axios.post(`${API}/system/send-consolidated-report`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.data.success) {
        toast.success(`✅ Informe enviado: ${response.data.total_caidas} caídas reportadas a soporte`);
        loadOutageReports();
        loadOutages();
      } else {
        toast.error(response.data.message || response.data.error || 'Error al enviar informe');
      }
    } catch (error) {
      console.error('Error enviando informe:', error);
      toast.error('Error al enviar informe consolidado');
    } finally {
      setSendingConsolidated(false);
    }
  };

  // Cargar reportes enviados a soporte
  const loadOutageReports = async () => {
    try {
      setLoadingReports(true);
      const response = await axios.get(`${API}/system/outage-reports`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.data.success) {
        setOutageReports(response.data.reports || []);
      }
    } catch (error) {
      console.error('Error cargando reportes:', error);
    } finally {
      setLoadingReports(false);
    }
  };

  // Reintentar envío de reportes pendientes
  const handleRetryReports = async () => {
    setRetryingReports(true);
    try {
      await retryPendingReports();
      loadOutages();
      loadOutageReports();
      toast.success('Reportes pendientes procesados');
    } catch (error) {
      toast.error('Error al reintentar reportes');
    } finally {
      setRetryingReports(false);
    }
  };

  // Cargar registro de caídas
  const loadOutages = () => {
    const localOutages = getLocalOutages();
    const stats = getOutageStats();
    setOutages(localOutages.reverse()); // Más recientes primero
    setOutageStats(stats);
  };

  // Limpiar registros antiguos
  const handleCleanOldOutages = () => {
    const deleted = cleanOldOutages();
    loadOutages();
    toast.success(`Se eliminaron ${deleted} registros de más de 30 días`);
  };

  // Limpiar todos los registros
  const handleClearAllOutages = () => {
    if (window.confirm('¿Estás seguro de eliminar TODOS los registros de caídas? Esta acción no se puede deshacer.')) {
      clearAllOutages();
      loadOutages();
      toast.success('Todos los registros han sido eliminados');
    }
  };

  // Exportar a CSV
  const handleExportCSV = () => {
    exportOutagesToCSV();
    toast.success('Archivo CSV descargado');
  };

  // Exportar a JSON
  const handleExportJSON = () => {
    exportOutagesToJSON();
    toast.success('Archivo JSON descargado');
  };

  const exportClasesCalendario = async () => {
    try {
      setExportingCalendar(true);
      const response = await fetch(`${API}/admin/export-clases-calendario`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Clases_Calendario_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('✅ CSV descargado. Impórtalo en Google Calendar');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al exportar');
      }
    } catch (error) {
      console.error('Error exporting calendar:', error);
      toast.error('Error de conexión');
    } finally {
      setExportingCalendar(false);
    }
  };

  const fetchSystemStatus = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/admin/system-status`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSystemData(data);
        setLastUpdate(new Date());
      } else {
        toast.error('Error al cargar estado del sistema');
      }
    } catch (error) {
      console.error('Error fetching system status:', error);
      toast.error('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSystemStatus();
    loadOutages(); // Cargar registro de caídas
    loadOutageReports(); // Cargar reportes enviados a soporte
    // Actualizar cada 5 minutos
    const interval = setInterval(() => {
      fetchSystemStatus();
      loadOutages();
      loadOutageReports();
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Cargar estado del banner PWA (solo admin)
  useEffect(() => {
    if (user?.rol !== 'admin') return;
    (async () => {
      try {
        const res = await axios.get(`${API}/public/banner-pwa-config`);
        setPwaBannerActivo(!!res.data?.activo);
      } catch (_e) {}
    })();
  }, [API, user]);

  const togglePwaBanner = async () => {
    setSavingPwaBanner(true);
    const nuevoEstado = !pwaBannerActivo;
    try {
      await axios.put(`${API}/admin/banner-pwa-config`, { activo: nuevoEstado });
      setPwaBannerActivo(nuevoEstado);
      toast.success(nuevoEstado ? 'Banner PWA activado en el dashboard' : 'Banner PWA desactivado');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo cambiar el estado');
    } finally {
      setSavingPwaBanner(false);
    }
  };

  const getStatusColor = (statusCode) => {
    switch(statusCode) {
      case 2: return 'bg-green-500';
      case 8:
      case 9: return 'bg-red-500';
      case 0: return 'bg-gray-400';
      default: return 'bg-yellow-500';
    }
  };

  const getStatusIcon = (statusCode) => {
    switch(statusCode) {
      case 2: return <CheckCircle2 className="h-5 w-5 text-green-600" />;
      case 8:
      case 9: return <XCircle className="h-5 w-5 text-red-600" />;
      default: return <AlertCircle className="h-5 w-5 text-yellow-600" />;
    }
  };

  const formatUptime = (ratio) => {
    return `${ratio.toFixed(2)}%`;
  };

  const formatDate = (timestamp) => {
    if (!timestamp || timestamp === "0") return 'Nunca';
    const date = new Date(parseInt(timestamp) * 1000);
    return date.toLocaleString('es-ES');
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return '0s';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${secs}s`;
    return `${secs}s`;
  };

  if (loading && !systemData) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
          <span className="ml-3 text-gray-600">Cargando estado del sistema...</span>
        </div>
      </div>
    );
  }

  if (!systemData || !systemData.success) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-6">
            <div className="text-center">
              <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Error al cargar datos</h3>
              <p className="text-gray-600 mb-4">No se pudo conectar con el servicio de monitoreo</p>
              <Button onClick={fetchSystemStatus}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Reintentar
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { stats, monitors } = systemData;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Activity className="h-8 w-8 text-blue-600" />
            Estado del Sistema
          </h1>
          <p className="text-gray-600 mt-1">Monitoreo en tiempo real de todos los servicios</p>
        </div>
        <Button onClick={fetchSystemStatus} disabled={loading} variant="outline">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualizar
        </Button>
      </div>

      {/* Estadísticas Generales */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Monitores</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total_monitors}</p>
              </div>
              <Activity className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Operativos</p>
                <p className="text-2xl font-bold text-green-600">{stats.monitors_up}</p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Caídos</p>
                <p className="text-2xl font-bold text-red-600">{stats.monitors_down}</p>
              </div>
              <XCircle className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Uptime Promedio</p>
                <p className="text-2xl font-bold text-blue-600">{formatUptime(stats.avg_uptime)}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lista de Monitores */}
      <Card>
        <CardHeader>
          <CardTitle>Servicios Monitoreados</CardTitle>
          <CardDescription>
            Estado actual de todos los servicios. Última actualización: {lastUpdate?.toLocaleTimeString('es-ES')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {monitors.map((monitor) => (
              <Card key={monitor.id} className="border-l-4" style={{ borderLeftColor: monitor.status_code === 2 ? '#10b981' : '#ef4444' }}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        {getStatusIcon(monitor.status_code)}
                        <h3 className="font-semibold text-lg">{monitor.name}</h3>
                        <Badge variant={monitor.status_code === 2 ? "success" : "destructive"}>
                          {monitor.status}
                        </Badge>
                        <Badge variant="outline">{monitor.type}</Badge>
                      </div>
                      
                      <p className="text-sm text-gray-600 mb-3">{monitor.url}</p>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500">Uptime</p>
                          <p className="font-semibold text-gray-900">{formatUptime(monitor.uptime_ratio)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Tiempo Respuesta</p>
                          <p className="font-semibold text-gray-900">
                            {monitor.response_time ? `${monitor.response_time}ms` : 'N/A'}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500">Última Caída</p>
                          <p className="font-semibold text-gray-900">
                            {formatDate(monitor.last_down_time)}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500">Monitor ID</p>
                          <p className="font-semibold text-gray-900">#{monitor.id}</p>
                        </div>
                      </div>

                      {/* Logs recientes */}
                      {monitor.logs && monitor.logs.length > 0 && (
                        <div className="mt-4 pt-4 border-t">
                          <p className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                            <AlertTriangle className="h-4 w-4 text-amber-500" />
                            Incidentes Recientes ({monitor.logs.length}):
                          </p>
                          <div className="space-y-2">
                            {monitor.logs.slice(0, 15).map((log, idx) => (
                              <div
                                key={idx}
                                className={`flex items-start gap-2 text-sm p-2 rounded-lg border ${
                                  log.type === 'down'
                                    ? 'bg-red-50 border-red-200'
                                    : 'bg-green-50 border-green-200'
                                }`}
                                data-testid={`outage-log-${idx}`}
                              >
                                <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${log.type === 'down' ? 'bg-red-500' : 'bg-green-500'}`}></div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5">
                                    <span className={`font-semibold ${log.type === 'down' ? 'text-red-700' : 'text-green-700'}`}>
                                      {log.type === 'down' ? '🔴 Caída' : '✅ Recuperado'}
                                    </span>
                                    {log.reason && (
                                      <span
                                        className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border ${
                                          log.type === 'down'
                                            ? 'bg-red-100 text-red-800 border-red-300'
                                            : 'bg-green-100 text-green-800 border-green-300'
                                        }`}
                                      >
                                        {log.reason}
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-xs text-gray-600 mt-0.5">
                                    {formatDate(log.datetime)}
                                    {log.duration > 0 && (
                                      <span className="ml-2 text-gray-500">
                                        · Duración: <strong>{formatDuration(log.duration)}</strong>
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                          {monitor.logs.length > 15 && (
                            <p className="text-xs text-gray-500 italic mt-2 text-center">
                              Mostrando los 15 más recientes de {monitor.logs.length} totales
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Herramientas de Exportación */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-blue-600" />
            Herramientas de Exportación
          </CardTitle>
          <CardDescription>
            Exportar datos del sistema a formatos externos
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[280px] p-4 border rounded-lg bg-gradient-to-r from-blue-50 to-purple-50">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Calendar className="h-6 w-6 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900">Exportar Clases a Google Calendar</h4>
                  <p className="text-sm text-gray-600 mt-1 mb-3">
                    Genera un archivo CSV con todas las clases activas y sus links de Zoom. 
                    Luego impórtalo en Google Calendar.
                  </p>
                  <Button 
                    onClick={exportClasesCalendario} 
                    disabled={exportingCalendar}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {exportingCalendar ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Generando...
                      </>
                    ) : (
                      <>
                        <Download className="h-4 w-4 mr-2" />
                        Descargar CSV para Calendario
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Banner Instalación PWA — solo admin */}
      {user?.rol === 'admin' && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <i className="fas fa-mobile-screen-button text-indigo-600" />
              Banner "Instalá IBA Connect" (PWA)
            </CardTitle>
            <CardDescription>
              Mostrar u ocultar el banner del dashboard que invita a los usuarios a instalar
              IBA Connect como aplicación en su dispositivo.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between gap-4 p-4 border rounded-lg bg-gradient-to-r from-indigo-50 to-blue-50">
              <div className="flex items-start gap-3 flex-1">
                <div className="p-2 bg-indigo-100 rounded-lg">
                  <i className="fas fa-mobile-screen-button text-2xl text-indigo-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">
                    Estado actual: {pwaBannerActivo ? (
                      <span className="text-green-700">Activado</span>
                    ) : (
                      <span className="text-gray-500">Desactivado</span>
                    )}
                  </h4>
                  <p className="text-sm text-gray-600 mt-1">
                    {pwaBannerActivo
                      ? 'Los usuarios ven en el dashboard el banner para instalar la app.'
                      : 'El banner está oculto. Activalo para que los usuarios sepan que pueden instalar la app.'}
                  </p>
                </div>
              </div>
              <Button
                onClick={togglePwaBanner}
                disabled={savingPwaBanner}
                className={pwaBannerActivo
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-indigo-600 hover:bg-indigo-700'}
                data-testid="toggle-pwa-banner-btn"
              >
                {savingPwaBanner ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Guardando...
                  </>
                ) : pwaBannerActivo ? (
                  <>
                    <i className="fas fa-toggle-off mr-2" />
                    Desactivar banner
                  </>
                ) : (
                  <>
                    <i className="fas fa-toggle-on mr-2" />
                    Activar banner
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Registro de Caídas Detectadas */}
      <Card className="mt-6 border-l-4 border-l-orange-500">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-orange-600" />
                Registro de Caídas Detectadas
              </CardTitle>
              <CardDescription>
                Historial de errores 5XX y problemas de conexión detectados desde el navegador
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={loadOutages}>
                <RefreshCw className="h-4 w-4 mr-1" />
                Actualizar
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Estadísticas de caídas */}
          {outageStats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                <p className="text-sm text-orange-700">Total Registros</p>
                <p className="text-2xl font-bold text-orange-900">{outageStats.total}</p>
              </div>
              <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                <p className="text-sm text-red-700">Hoy</p>
                <p className="text-2xl font-bold text-red-900">{outageStats.today}</p>
              </div>
              <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                <p className="text-sm text-yellow-700">Última Semana</p>
                <p className="text-2xl font-bold text-yellow-900">{outageStats.lastWeek}</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <p className="text-sm text-blue-700">Último Mes</p>
                <p className="text-2xl font-bold text-blue-900">{outageStats.lastMonth}</p>
              </div>
            </div>
          )}

          {/* Resumen por código de error */}
          {outageStats && outageStats.byErrorCode && Object.keys(outageStats.byErrorCode).length > 0 && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm font-semibold text-gray-700 mb-2">Errores por Código:</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(outageStats.byErrorCode).map(([code, count]) => (
                  <Badge key={code} variant="secondary" className="bg-red-100 text-red-800">
                    Error {code}: {count}x
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Botones de acción */}
          <div className="flex flex-wrap gap-2 mb-4 p-3 bg-gray-50 rounded-lg">
            <Button variant="outline" size="sm" onClick={handleExportCSV} disabled={outages.length === 0}>
              <Download className="h-4 w-4 mr-1" />
              Exportar CSV
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportJSON} disabled={outages.length === 0}>
              <FileText className="h-4 w-4 mr-1" />
              Exportar JSON
            </Button>
            <Button variant="outline" size="sm" onClick={handleCleanOldOutages} disabled={outages.length === 0}>
              <Clock className="h-4 w-4 mr-1" />
              Limpiar +30 días
            </Button>
            <Button variant="destructive" size="sm" onClick={handleClearAllOutages} disabled={outages.length === 0}>
              <Trash2 className="h-4 w-4 mr-1" />
              Eliminar Todo
            </Button>
          </div>

          {/* Tabla de registros */}
          {outages.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-green-500" />
              <p className="font-medium">No hay caídas registradas</p>
              <p className="text-sm">El sistema registrará automáticamente cualquier error 5XX detectado</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-100 border-b">
                      <th className="text-left p-3 font-semibold">Fecha/Hora</th>
                      <th className="text-left p-3 font-semibold">Error</th>
                      <th className="text-left p-3 font-semibold hidden md:table-cell">Endpoint</th>
                      <th className="text-left p-3 font-semibold hidden lg:table-cell">Usuario</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(showAllOutages ? outages : outages.slice(0, 10)).map((outage, idx) => (
                      <tr key={outage.id || idx} className="border-b hover:bg-gray-50">
                        <td className="p-3">
                          <div className="font-medium text-gray-900">{outage.timestampLocal || new Date(outage.timestamp).toLocaleString('es-UY')}</div>
                        </td>
                        <td className="p-3">
                          <Badge variant="destructive" className="font-mono">
                            {outage.errorCode}
                          </Badge>
                        </td>
                        <td className="p-3 hidden md:table-cell">
                          <code className="text-xs bg-gray-100 px-2 py-1 rounded truncate max-w-[200px] block">
                            {outage.method} {outage.endpoint?.split('?')[0]?.substring(0, 40)}
                          </code>
                        </td>
                        <td className="p-3 hidden lg:table-cell text-gray-600">
                          {outage.userEmail || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {outages.length > 10 && (
                <div className="mt-4 text-center">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowAllOutages(!showAllOutages)}
                  >
                    {showAllOutages ? 'Mostrar menos' : `Ver todos (${outages.length} registros)`}
                  </Button>
                </div>
              )}
            </>
          )}

          {/* Última caída */}
          {outageStats?.lastOutage && (
            <div className="mt-4 p-3 bg-red-50 rounded-lg border border-red-200">
              <p className="text-sm text-red-700">
                <strong>Última caída detectada:</strong> {outageStats.lastOutage.timestampLocal || outageStats.lastOutage.timestamp}
                {' '}- Error {outageStats.lastOutage.errorCode} en {outageStats.lastOutage.endpoint?.split('?')[0]}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reportes Enviados a Soporte de Emergent */}
      <Card className="mt-6 border-l-4 border-l-purple-500">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-purple-600" />
                Reportes a Soporte de Emergent
              </CardTitle>
              <CardDescription>
                Sistema de reportes consolidados - UN solo email con todas las caídas
              </CardDescription>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button 
                onClick={handleSendConsolidatedReport}
                disabled={sendingConsolidated}
                className="bg-red-600 hover:bg-red-700"
              >
                {sendingConsolidated ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Enviar Informe Consolidado
              </Button>
              <Button 
                variant="destructive" 
                size="sm" 
                onClick={handleClearDbReports}
                disabled={clearingDbReports || outageReports.length === 0}
              >
                {clearingDbReports ? (
                  <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4 mr-1" />
                )}
                Borrar Todo BD
              </Button>
              <Button variant="outline" size="sm" onClick={loadOutageReports} disabled={loadingReports}>
                <RefreshCw className={`h-4 w-4 mr-1 ${loadingReports ? 'animate-spin' : ''}`} />
                Actualizar
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Aviso importante */}
          <div className="mb-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
            <p className="text-sm text-amber-800">
              <AlertTriangle className="inline h-4 w-4 mr-1" />
              <strong>Modo consolidado activo:</strong> Las caídas se registran automáticamente. 
              Usa el botón "Enviar Informe Consolidado" para enviar UN SOLO email con todas las caídas pendientes a soporte.
            </p>
          </div>

          {/* Estadísticas de reportes */}
          {outageStats && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <p className="text-sm text-green-700">Ya Reportadas</p>
                <p className="text-2xl font-bold text-green-900">{outageStats.reported}</p>
              </div>
              <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                <p className="text-sm text-yellow-700">Pendientes de Informe</p>
                <p className="text-2xl font-bold text-yellow-900">{outageStats.pending}</p>
              </div>
              <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                <p className="text-sm text-purple-700">Total en BD</p>
                <p className="text-2xl font-bold text-purple-900">{outageReports.length}</p>
              </div>
            </div>
          )}

          {/* Lista de reportes enviados */}
          {loadingReports ? (
            <div className="text-center py-8">
              <RefreshCw className="h-8 w-8 animate-spin mx-auto text-purple-500 mb-3" />
              <p className="text-gray-600">Cargando reportes...</p>
            </div>
          ) : outageReports.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Mail className="h-12 w-12 mx-auto mb-3 text-gray-400" />
              <p className="font-medium">No hay reportes enviados</p>
              <p className="text-sm">Cuando se detecte una caída, se enviará automáticamente un email a soporte de Emergent</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-purple-50 border-b">
                    <th className="text-left p-3 font-semibold">Fecha Envío</th>
                    <th className="text-left p-3 font-semibold">Error</th>
                    <th className="text-left p-3 font-semibold hidden md:table-cell">Endpoint</th>
                    <th className="text-left p-3 font-semibold">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {outageReports.slice(0, 20).map((report, idx) => (
                    <tr key={report.id || idx} className="border-b hover:bg-purple-50/50">
                      <td className="p-3">
                        <div className="font-medium text-gray-900">
                          {report.fecha_envio_local || report.timestamp_local || 
                           (report.fecha_envio || report.fecha_registro || report.fecha_intento 
                            ? new Date(report.fecha_envio || report.fecha_registro || report.fecha_intento).toLocaleString('es-UY')
                            : 'N/A')}
                        </div>
                      </td>
                      <td className="p-3">
                        <Badge 
                          variant={report.estado === 'enviado' ? 'default' : 'destructive'} 
                          className={`font-mono ${report.estado === 'enviado' ? 'bg-purple-100 text-purple-800' : ''}`}
                        >
                          {report.error_code}
                        </Badge>
                      </td>
                      <td className="p-3 hidden md:table-cell">
                        <code className="text-xs bg-gray-100 px-2 py-1 rounded truncate max-w-[200px] block">
                          {report.endpoint?.substring(0, 40)}
                        </code>
                      </td>
                      <td className="p-3">
                        {report.estado === 'enviado' ? (
                          <Badge className="bg-green-100 text-green-800">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Enviado
                          </Badge>
                        ) : (
                          <Badge variant="destructive">
                            <XCircle className="h-3 w-3 mr-1" />
                            Fallido
                          </Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Info de destino */}
          <div className="mt-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
            <p className="text-sm text-purple-700">
              <Mail className="inline h-4 w-4 mr-1" />
              <strong>Destinatario:</strong> support@emergent.sh (Soporte de Emergent)
              <br />
              <span className="text-xs text-purple-600">
                Los emails incluyen: código de error, endpoint afectado, usuario, fecha/hora, y detalles técnicos para diagnóstico.
              </span>
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Footer Info */}
      <div className="mt-6 text-center text-sm text-gray-500">
        <Clock className="inline h-4 w-4 mr-1" />
        Los datos se actualizan automáticamente cada 5 minutos
      </div>
    </div>
  );
};

export default EstadoSistema;
